import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {sendEvarData, logPageLoadEvents} from '@ibibo/analytics';
import FileUploaded from '../FileUploaded/FileUploaded';
import FileLoader from '../FileUploader/FileUploader';
import TextInput from '../TextInput/TextInput';
import UploadedFiles from '../UploadedFiles/UploadedFiles';
import Spinner from '../Spinner/Spinner';
import PassportTooltip from '../PassportTooltip/PassportTooltip';
import PassportTooltipImage from '../assets/passport.png';

import * as S from './Steps.styles';
import * as C from '../../../OnboardingNew/Common.styles';

const Step2 = ({
  setPassportDetails,
  passportDetails,
  approveHandler,
  isLoading,
  setIsLoading,
  disableSubmitButton,
  approve,
  setApprove,
  disableInput,
  setDisabledInput,
}) => {
  const winSize = window.screen.width;

  const [filePath, setFilePath] = useState('');
  const [fileType, setFileType] = useState('');
  const [imageSizeExceededError, setImageSizeExceededError] = useState('');
  const [tooltip, setTooltip] = useState(true);
  const [fileName, setFileName] = useState('');

  const closeTooltip = () => setTooltip(false);

  const getFileInfo = info => {
    setFileType(info[0].type);
  };

  const selectFile = (val, fileData) => {
    setFilePath(val);
    setFileName(fileData?.name);
    const obj = passportDetails;
    obj['passport_doc'] = fileData;
    setPassportDetails({...obj});
    logPageLoadEvents('onboarding:signup:landing', {
      eVar15: 'onboarding:personaldetails|mypartner',
      channel: 'onboarding|mypartner',
      prop24: 'onboarding|mypartner',
      eVar24: 'onboarding|mypartner',
      prop27: '/agent/upload-license',
      eVar37: 'domain_sbu:mypartner|domain_country:ae|domain_currency:aed|domain_language:eng',
      prop54: 'myPartner_onboarding_upload',
    });
  };

  const onApprove = (uploadAgain = false) => {
    if (!uploadAgain)
      logPageLoadEvents('onboarding:signup:landing', {
        eVar15: 'onboarding:personaldetails|mypartner',
        channel: 'onboarding|mypartner',
        prop24: 'onboarding|mypartner',
        eVar24: 'onboarding|mypartner',
        prop27: '/agent/upload-license',
        eVar37: 'domain_sbu:mypartner|domain_country:ae|domain_currency:aed|domain_language:eng',
        prop54: 'myPartner_onboarding_uploadApprove',
      });
    else {
      logPageLoadEvents('onboarding:signup:landing', {
        eVar15: 'onboarding:personaldetails|mypartner',
        channel: 'onboarding|mypartner',
        prop24: 'onboarding|mypartner',
        eVar24: 'onboarding|mypartner',
        prop27: '/agent/upload-license',
        eVar37: 'domain_sbu:mypartner|domain_country:ae|domain_currency:aed|domain_language:eng',
        prop54: 'myPartner_onboarding_uploadRetry',
      });
    }
    setApprove(true);
    approveHandler();
  };

  const handleDropdownSelection = e => {
    if (!e.target.value) return;
    const obj = passportDetails;
    obj['title'] = e.target.value;
    setPassportDetails({...obj});
  };

  const onClose = () => {
    logPageLoadEvents('onboarding:signup:landing', {
      eVar15: 'onboarding:personaldetails|mypartner',
      channel: 'onboarding|mypartner',
      prop24: 'onboarding|mypartner',
      eVar24: 'onboarding|mypartner',
      prop27: '/agent/upload-license',
      eVar37: 'domain_sbu:mypartner|domain_country:ae|domain_currency:aed|domain_language:eng',
      prop54: 'myPartner_onboarding_uploadRemove',
    });
    setApprove(false);
    setDisabledInput(false);
  };

  return (
    <S.StepCont>
      <S.Heading>Create myPartner Account</S.Heading>
      <S.SubHeading>Tell us a little bit about your business</S.SubHeading>
      <S.LegalName>
        <S.TitleWrapper>
          <S.CommonLabel>Title</S.CommonLabel>
          <C.CommonSelect
            value={passportDetails?.title}
            onChange={handleDropdownSelection}
            disabled={disableInput}
          >
            <option value="">Title</option>
            <option value="Mr.">Mr.</option>
            <option value="Mrs.">Mrs.</option>
            <option value="Ms.">Ms.</option>
          </C.CommonSelect>
        </S.TitleWrapper>
        <TextInput
          labelText={'Legal Owner Name'}
          placeholder="Given Name(s)"
          haserror={false}
          mobileNumber={false}
          inputType={'text'}
          endIcon="sprite ic_eye"
          setterFunction={setPassportDetails}
          name={'givenName'}
          component={'passportUploadDetails'}
          setterObj={passportDetails}
          withoutLabel={true}
          setMargin={false}
          disabled={disableInput}
        />
        <TextInput
          placeholder="Surname(optional)"
          classes={'append19Top'}
          haserror={false}
          mobileNumber={false}
          inputType={'text'}
          endIcon="sprite ic_eye"
          setterFunction={setPassportDetails}
          name={'surname'}
          component={'passportUploadDetails'}
          setterObj={passportDetails}
          disabled={disableInput}
        />
      </S.LegalName>
      <TextInput
        labelText={'Passport Number'}
        placeholder="Passport Number"
        classes={'appendTop30'}
        haserror={false}
        mobileNumber={false}
        inputType={'text'}
        endIcon="sprite ic_eye"
        setterFunction={setPassportDetails}
        name={'passportNumber'}
        component={'passportUploadDetails'}
        setterObj={passportDetails}
        disabled={disableInput}
      />
      <TextInput
        labelText={'Nationality'}
        placeholder="Nationality"
        classes={'appendTop30'}
        mobileNumber={false}
        setterFunction={setPassportDetails}
        name={'nationality'}
        component={'passportUploadDetails'}
        setterObj={passportDetails}
        disabled={disableInput}
      />
      <TextInput
        labelText={'Mobile Number'}
        placeholder="Mobile Number"
        classes={'appendTop30'}
        mobileNumber={true}
        setterFunction={setPassportDetails}
        name={'mobile'}
        component={'passportUploadDetails'}
        setterObj={passportDetails}
        showOnlyUaeNumber={true}
        disabled={disableInput}
        stringValue={passportDetails?.mobile || ''}
      />
      <S.TopContent>
        <S.TopContentLft>
          <S.TopContentTitle>Upload Passport of Legal Owner</S.TopContentTitle>
          <S.TopContentSubTitle>
            Ensure that the image isn't blurred and{' '}
            <span>all the details including Passport holder name and MRZ code</span> are clear.
          </S.TopContentSubTitle>
          {!filePath && tooltip && (
            <PassportTooltip
              imgUrl={PassportTooltipImage}
              text="Passport MRZ Code"
              onClose={closeTooltip}
            />
          )}
        </S.TopContentLft>
        <S.TopContentRht>
          {!isLoading && approve ? (
            <FileUploaded
              closeIcon={true}
              title={fileName}
              //fileSize="851KB"
              onClose={onClose}
            />
          ) : (
            <React.Fragment>
              <FileLoader
                fileTypes={['.png, .jpeg, .pdf']}
                onFileSelect={selectFile}
                getFileInfo={getFileInfo}
                setImageSizeExceededError={setImageSizeExceededError}
              >
                <p className="font10">
                  Upload <span className="latoBold">Passport</span>
                </p>
                <p className="font10">PDF, JPEG Only | Below 5MB</p>
              </FileLoader>
              {filePath && (
                <p className="font11 greyText appendTop5">
                  By proceeding, you agree to MakeMyTrip's T&Cs and Privacy Policy
                </p>
              )}
            </React.Fragment>
          )}
          {imageSizeExceededError && <S.ErrorMsg>{imageSizeExceededError}</S.ErrorMsg>}
        </S.TopContentRht>
      </S.TopContent>
      {filePath && !approve && (
        <UploadedFiles
          fileType={fileType}
          filePath={filePath}
          fileTypes={['.png, .jpeg, .pdf']}
          onFileSelect={selectFile}
          getFileInfo={getFileInfo}
          onApprove={onApprove}
          disableSubmitButton={disableSubmitButton}
        />
      )}
      {isLoading && (
        <S.SpinnerCont>
          <Spinner
            width={winSize >= 768 ? '60px' : '32px'}
            height={winSize >= 768 ? '60px' : '32px'}
            borderWidth={winSize >= 768 ? '8px' : '4px'}
          />
        </S.SpinnerCont>
      )}
    </S.StepCont>
  );
};

export default Step2;
